import { useState } from "react";
import { IconPin, IconRoute, IconCheckCircle, IconPhone, IconChat } from "./Icons";

/**
 * Returns a Cloudinary URL resized to 600px wide for card thumbnails.
 * Inserts transformation params after "/upload/" in the CDN URL.
 * Non-Cloudinary URLs are returned unchanged.
 *
 * 600px covers the widest card (~280px) at 2× for retina displays.
 * The detail modal uses the original full-size URL directly.
 */
function getCardThumb(url) {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/w_600,c_limit,q_auto,f_auto/");
}

function BusinessCard({ business, onSelect }) {
  const [index, setIndex] = useState(0);

  if (!business) return null;

  const resolveImage = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item.url || item.image || item.imageUrl || "";
  };

  const rawImages = Array.isArray(business.images)
    ? business.images
    : business.images
    ? [business.images]
    : business.image || business.imageUrl
    ? [business.image || business.imageUrl]
    : ["/no-image.svg"];

  const images = rawImages.map(resolveImage).filter(Boolean);
  if (images.length === 0) images.push("/no-image.svg");

  const cleanPhone = business.phone?.replace(/[^0-9]/g, "");

  const distanceKm =
    business.distance !== undefined
      ? (business.distance / 1000).toFixed(1)
      : null;

  const altText = `${business.businessName} - ${business.category} in ${business.city}`;

  const nextImage = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <a
      href={`/business/${business.slug || business._id}`}
      className="business-card"
      aria-label={`View details for ${business.businessName || "business"}`}
      onClick={(e) => {
        e.preventDefault();
        onSelect && onSelect(business);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect && onSelect(business);
        }
      }}
    >
      {/* IMAGE */}
      <div className="business-image-container">
        <img
          src={getCardThumb(images[index])}
          alt={altText}
          className="business-card-image"
          loading="lazy"
          decoding="async"
        />

        {images.length > 1 && (
          <>
            <button
              className="business-slider-btn left"
              onClick={prevImage}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              className="business-slider-btn right"
              onClick={nextImage}
              aria-label="Next image"
            >
              ›
            </button>

            <div className="business-dots" aria-hidden="true">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`business-dot ${i === index ? "active" : ""}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* INFO */}
      <div className="business-info">
        {business.category && (
          <span className="business-category-tag">{business.category}</span>
        )}

        {/* Name + trust badges on the same line */}
        <div className="business-name-row">
          <h3 className="business-title" title={business.businessName || "No Name"}>
            {business.businessName || "No Name"}
          </h3>
          {business.verified && (
            <span className="verified-badge">
              <IconCheckCircle size={11} />
              Verified
            </span>
          )}
          {business.mobileVerified && (
            <span className="mobile-verified-badge-card">&#10003; Mobile</span>
          )}
        </div>

        <p className="business-location">
          <span className="icon-row">
            <IconPin size={13} />
            {business.city || "Unknown"}
          </span>
        </p>

        {distanceKm !== null && (
          <p className="business-distance">
            <span className="icon-row">
              <IconRoute size={13} />
              {distanceKm === "0.0" ? "Nearby" : `${distanceKm} km away`}
            </span>
          </p>
        )}

        {/* ACTIONS */}
        <div
          className="business-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {cleanPhone && (
            <a
              href={`tel:${cleanPhone}`}
              className="business-call-btn"
              aria-label={`Call ${business.businessName}`}
            >
              <IconPhone size={14} />
              Call
            </a>
          )}

          {cleanPhone && (
            <a
              href={`https://wa.me/${cleanPhone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="business-whatsapp-btn"
              aria-label={`WhatsApp ${business.businessName}`}
            >
              <IconChat size={14} />
              WhatsApp
            </a>
          )}
        </div>
      </div>
    </a>
  );
}

export default BusinessCard;
